import { GENERIC_ERROR_MESSAGE } from '../constants';
import {
    exchangeTokenKeyForAuthenticationToken,
    initialize,
    invoke,
    join,
} from '../services/network';
import { getOptionsFromURL } from '../services/options';
import type { UpdateState } from '../types/functional';
import { type AppState, PageType } from '../types/models';
import type { ExecutionMode, StartType } from '../types/public/models';
import { reportError } from '../utils/errors';
import useEffectOnce from './useEffectOnce';

const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

const removeAuthorizationTokenFromLocation = (location: Location) => {
    const url = new URL(location.toString());
    url.searchParams.delete('initialization');
    url.searchParams.delete('authorization');
    return url;
};

const replaceUrl = (url: URL) => {
    try {
        history.replaceState(null, '', url);
    } catch (error) {
        reportError((error as DOMException).message);
    }
};

/** Initializes or joins a flow state and updates the application state with the API response */
export default function useInitialization(
    appState: AppState,
    updateState: UpdateState,
    setErrorState: (errorMessage: string) => void,
    executionMode: ExecutionMode = null,
): void {
    useEffectOnce(() => {
        /**
         * Initialization
         */
        (async () => {
            const { tenantId, stateId } = appState;

            const startType: StartType =
                tenantId !== null && stateId !== null && stateId !== '' ? 'JOIN' : 'INITIALIZE';

            replaceUrl(removeAuthorizationTokenFromLocation(window.location));

            try {
                updateState({ ...appState, isLoading: true });

                let authenticationToken = appState.authenticationToken;

                if (
                    appState.options.authenticationToken &&
                    !jwtPattern.test(appState.options.authenticationToken)
                ) {
                    authenticationToken = await exchangeTokenKeyForAuthenticationToken(
                        appState.tenantId,
                        appState.options.authenticationToken,
                    );

                    updateState({ ...appState, authenticationToken });
                }

                let newState =
                    startType === 'JOIN'
                        ? await join({
                              ...appState,
                              authenticationToken,
                          })
                        : await initialize({
                              appState: {
                                  ...appState,
                                  authenticationToken,
                              },
                              playerUrl: window.location.toString(),
                              executionMode,
                          });

                if (
                    startType === 'INITIALIZE' &&
                    newState.statusCode !== '401' &&
                    newState.statusCode !== '403'
                ) {
                    // The user is authenticated and initialize has created a state so now we can invoke to start
                    newState = await invoke({
                        appState: newState,
                        invokeType: 'FORWARD',
                    });
                }

                if (appState.options.allowLocationControl && newState.runFlowUri) {
                    const options = getOptionsFromURL();

                    if (!options.stateId) {
                        replaceUrl(new URL(newState.runFlowUri));
                    }
                }

                if (newState.pageType === PageType.Modal) {
                    updateState({ ...appState, isLoading: false });
                }

                updateState({ ...newState, isLoading: false });

                // selectedOutcomeId can be not null here if its passed in via the selectedOutcomeId querystring parameter (or options),
                // for example via a link generated from the Email with Decision message action
                if (appState.selectedOutcomeId) {
                    updateState({ ...newState, isLoading: true });

                    newState = await invoke({
                        appState: newState,
                        invokeType: 'FORWARD',
                        messageActionRequest: {
                            selectedOutcomeId: appState.selectedOutcomeId,
                        },
                    });

                    if (appState.options.allowLocationControl) {
                        const urlSearchParams = new URLSearchParams(window.location.search);
                        urlSearchParams.delete('selectedOutcomeId');
                        window.history.replaceState(
                            appState.stateToken,
                            'Title',
                            `${window.location.pathname}?${urlSearchParams.toString()}`,
                        );
                    }

                    updateState({
                        ...newState,
                        selectedOutcomeId: null,
                        isLoading: false,
                    });
                }
            } catch (ex: unknown) {
                reportError(ex as Error);
                setErrorState(GENERIC_ERROR_MESSAGE);
            }
        })();
    });
}
